import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';

import Breadcrumb from '../components/breadcrumb';
import { Picture, Responsive, ctaTracker, landingImage } from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import NewsletterTile from '../components/newsletter/newsletterTile';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import SETTINGS from '../settings';

// How to Make a Photo Book guide (docs/how-to-make-a-photo-book.md). A visual layout guide with
// a recurring "advisor" voice. Copy follows the Pencil frames "How to Make a Photo Book — New
// Page" / "— Mobile". Layout schematics are drawn to scale from the Softcover template.
const CAMPAIGN = 'how_to_make_a_photo_book';
const track = ctaTracker(CAMPAIGN);

const ID = {
  CHECKS: 'checks',
  BEFORE_AFTER: 'before-after',
  LAYOUTS: 'layouts',
  BIG_OR_SMALL: 'big-or-small',
  PAIRS: 'pairs',
  CROP: 'crop',
  PACE: 'pace',
  CAPTIONS: 'captions',
  EDITOR: 'editor',
  FAQ: 'faq',
};

// Main web path, same as the other photo book guides until a direct editor entry is confirmed.
const BOOK_HREF = '/photo-books';
// The blog article covers getting photos off the phone, this guide owns the layout side.
const CAMERA_ROLL_ARTICLE =
  '/blog/how-to-make-a-photo-book-from-your-camera-roll-without-waiting-for-perfect/';

const APP_STORE_HREF = (() => {
  const url = new URL(SETTINGS.PHOTO_BOOKS_APP_STORE_URL);
  url.searchParams.set('utm_source', 'website');
  url.searchParams.set('utm_medium', 'landing');
  url.searchParams.set('utm_campaign', CAMPAIGN);
  return url.href;
})();

const IMAGES = {
  hero: landingImage('hc-hands-open'),
  captions: landingImage('sc-captions-open'),
  final: landingImage('hc-couch-open'),
};

const SCREENS = {
  web: '/static/img/web-book-layout-panel.webp',
  app: '/static/img/app-book-layout.webp',
};

// Prepared source images live in parabo-product-images, see docs/unsplash-assets.md.
const PHOTO = {
  beachDog: 'landing/photo-book-guide/beach-dog',
  beachWide: 'landing/photo-book-guide/beach-wide',
  dunes: 'landing/photo-book-guide/dunes',
  beachSelfie: 'landing/photo-book-guide/beach-selfie',
  cake: 'landing/photo-book-guide/cake',
  kitchen: 'landing/photo-book-guide/kitchen',
  baking: 'landing/photo-book-guide/baking',
  bow: 'landing/photo-book-guide/bow',
  grandpaBlossom: 'landing/photo-book-guide/grandpa-blossom',
  grandpaGarden: 'landing/photo-book-guide/grandpa-garden',
  gardenWork: 'landing/photo-book-guide/garden-work',
  hills: 'landing/photo-book-guide/hills',
  tree: 'landing/photo-book-guide/tree',
  forest: 'landing/photo-book-guide/forest',
  group: 'landing/photo-book-guide/group',
  family: 'landing/photo-book-guide/family',
  dog: 'landing/photo-book-guide/dog',
  hat: 'landing/photo-book-guide/hat',
  candle: 'landing/photo-book-guide/candle',
  puppy: 'landing/photo-book-guide/puppy',
  coast: 'landing/photo-book-guide/coast',
  couple: 'landing/photo-book-guide/couple',
  kids: 'landing/photo-book-guide/kids',
  lake: 'landing/photo-book-guide/lake',
  sunset: 'landing/photo-book-guide/sunset',
};

// Photo and caption frames as [left, top, width, height] in % of the page, measured from the
// softcover-photo-book template (parabo-templates 6.0.0) on a 15.88 cm page.
const GEOMETRY = {
  coverBorder: { photos: [[5.7, 6.3, 88, 87.4]], cover: true },
  coverFull: { photos: [[0, 0, 100, 100]], cover: true },
  coverBorderTitle: { photos: [[10.4, 12.2, 77.1, 75.5]], caption: [10.4, 87.8, 77.1, 11.4], cover: true },
  coverFullTitle: { photos: [[0, 0, 100, 69.3]], caption: [0, 75.5, 100, 18.9], cover: true },
  back: { photos: [], logo: [43, 82, 14, 3], cover: true },
  full: { photos: [[0, 0, 100, 100]] },
  square: { photos: [[10.7, 10.7, 78.6, 78.6]] },
  portrait: { photos: [[19.3, 9.1, 61.4, 81.8]] },
  landscape: { photos: [[9.1, 19.3, 81.8, 61.4]] },
  grid: {
    photos: [
      [8, 8, 41.1, 41.1],
      [50.9, 8, 41.1, 41.1],
      [8, 50.9, 41.1, 41.1],
      [50.9, 50.9, 41.1, 41.1],
    ],
  },
  sideBySide: {
    photos: [
      [8, 23.6, 39.7, 53],
      [52.3, 23.6, 39.7, 53],
    ],
  },
  stacked: {
    photos: [
      [14.9, 8, 70.1, 39.5],
      [14.9, 52.5, 70.1, 39.5],
    ],
  },
  bigLeft: {
    photos: [
      [8, 8, 51.4, 84],
      [61.2, 8, 30.8, 41.1],
      [61.2, 50.9, 30.8, 41.1],
    ],
  },
  bigRight: {
    photos: [
      [40.7, 8, 51.4, 84],
      [8, 8, 30.8, 41.1],
      [8, 50.9, 30.8, 41.1],
    ],
  },
  row: {
    photos: [
      [8, 30, 26.7, 40.1],
      [36.6, 30, 26.7, 40.1],
      [65.3, 30, 26.7, 40.1],
    ],
  },
  fullCaption: { photos: [[0, 0, 100, 66.7]], caption: [8, 74.7, 84, 17.3] },
  portraitCaption: { photos: [[8, 8, 62, 84]], caption: [71.9, 8, 20.1, 84] },
  stackedCaption: {
    photos: [
      [8, 8, 84, 30.1],
      [8, 39.9, 84, 30.1],
    ],
    caption: [8, 71.8, 84, 20.1],
  },
};

const box = ([left, top, width, height]) => ({
  left: `${left}%`,
  top: `${top}%`,
  width: `${width}%`,
  height: `${height}%`,
});

// One book page. With `photos` it shows a filled example, without them the empty schematic.
const Page = ({ layout, photos, caption, className }) => {
  const geometry = GEOMETRY[layout];
  return (
    <span
      className={classNames('htm-page', { 'htm-page--cover': geometry.cover }, className)}
      aria-hidden="true"
    >
      {geometry.photos.map((frame, index) =>
        photos ? (
          <img
            key={index}
            className="htm-slot"
            style={box(frame)}
            src={getCloudinaryUrl({ imageKey: photos[index], type: 'w480' })}
            alt=""
            loading="lazy"
          />
        ) : (
          <span key={index} className="htm-slot htm-slot--empty" style={box(frame)}>
            <PdpIcon name="image" size={16} />
          </span>
        ),
      )}
      {geometry.caption &&
        (caption ? (
          <span className="htm-caption" style={box(geometry.caption)}>
            {caption}
          </span>
        ) : (
          <span className="htm-caption htm-caption--lines" style={box(geometry.caption)}>
            <span />
            <span />
          </span>
        ))}
      {geometry.logo && <span className="htm-logo" style={box(geometry.logo)} />}
    </span>
  );
};

Page.propTypes = {
  layout: PropTypes.oneOf(Object.keys(GEOMETRY)).isRequired,
  photos: PropTypes.arrayOf(PropTypes.string),
  caption: PropTypes.string,
  className: PropTypes.string,
};

const pageShape = PropTypes.shape({
  layout: PropTypes.string.isRequired,
  photos: PropTypes.arrayOf(PropTypes.string),
  caption: PropTypes.string,
});

const Spread = ({ left, right, className }) => (
  <span className={classNames('htm-spread', className)}>
    <Page {...left} />
    <Page {...right} />
  </span>
);

Spread.propTypes = {
  left: pageShape.isRequired,
  right: pageShape.isRequired,
  className: PropTypes.string,
};

const ArrowButton = ({ href, children, onClick, className }) => (
  <Link href={href} className={classNames('lnd-btn', className)} onClick={onClick}>
    {children}
    <PdpIcon name="arrowRight" size={18} />
  </Link>
);

ArrowButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const AdvisorTip = ({ children, className }) => (
  <aside className={classNames('htm-tip', className)}>
    <p className="htm-tip-label">
      <PdpIcon name="lightbulb" size={16} />
      Advisor tip
    </p>
    <p className="htm-tip-text">{children}</p>
  </aside>
);

AdvisorTip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const Eyebrow = ({ number, children }) => (
  <p className="htm-eyebrow">
    {number} · {children}
  </p>
);

Eyebrow.propTypes = {
  number: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// Eyebrow, H2 and intro. Phone intro copy comes from the mobile frame.
const SectionHead = ({ number, eyebrow, title, intro, phoneIntro }) => (
  <div className="htm-head">
    <Eyebrow number={number}>{eyebrow}</Eyebrow>
    <h2 className="htm-h2">{title}</h2>
    {intro && (
      <p className="htm-intro">
        <Responsive desktop={intro} phone={phoneIntro} />
      </p>
    )}
  </div>
);

SectionHead.propTypes = {
  number: PropTypes.string.isRequired,
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  intro: PropTypes.string,
  phoneIntro: PropTypes.string,
};

//
// Outline and section nav
//

const OUTLINE = [
  {
    group: 'Before you start',
    items: [
      { id: ID.CHECKS, label: 'Six checks before you order' },
      { id: ID.BEFORE_AFTER, label: 'Same photos, two spreads' },
    ],
  },
  { group: 'The layouts', items: [{ id: ID.LAYOUTS, label: 'Every layout in the book' }] },
  {
    group: 'Layout ideas',
    items: [
      { id: ID.BIG_OR_SMALL, label: 'One big photo, or a few small ones' },
      { id: ID.PAIRS, label: 'Pair facing pages' },
      { id: ID.CROP, label: 'Crop with care' },
      { id: ID.PACE, label: 'Pace the book like a story' },
      { id: ID.CAPTIONS, label: 'Add words only when they help' },
    ],
  },
  {
    group: 'Make it',
    items: [
      { id: ID.EDITOR, label: 'Change layouts in the editor' },
      { id: ID.FAQ, label: 'Questions' },
    ],
  },
];
const SECTION_IDS = OUTLINE.flatMap(({ items }) => items.map(({ id }) => id));
const sectionNumber = (id) => String(SECTION_IDS.indexOf(id) + 1).padStart(2, '0');

const IDEA_IDS = [ID.BIG_OR_SMALL, ID.PAIRS, ID.CROP, ID.PACE, ID.CAPTIONS];

// Desktop groups the five layout ideas under one link, phones get a chip per section.
const NAV_LINKS = [
  { ids: [ID.CHECKS], label: 'Six checks' },
  { ids: [ID.BEFORE_AFTER], label: 'Before and after' },
  { ids: [ID.LAYOUTS], label: 'Layouts' },
  { ids: IDEA_IDS, label: 'Layout ideas' },
  { ids: [ID.EDITOR], label: 'In the editor' },
  { ids: [ID.FAQ], label: 'FAQ' },
];

const NAV_CHIPS = [
  { id: ID.CHECKS, label: 'Six checks' },
  { id: ID.BEFORE_AFTER, label: 'Before and after' },
  { id: ID.LAYOUTS, label: 'Layouts' },
  { id: ID.BIG_OR_SMALL, label: 'Big or small' },
  { id: ID.PAIRS, label: 'Pairs' },
  { id: ID.CROP, label: 'Crop' },
  { id: ID.PACE, label: 'Pace' },
  { id: ID.CAPTIONS, label: 'Captions' },
  { id: ID.EDITOR, label: 'Editor' },
  { id: ID.FAQ, label: 'FAQ' },
];

const OUTLINE_ID = 'in-this-guide';

// Active section = the last one whose top has passed 30% of the viewport (same rule as Year in
// Photos). The bar shows once the outline has scrolled away. The progress line is written to a
// CSS variable directly, so scrolling does not re-render the page.
const useSectionNav = (progressRef) => {
  const [state, setState] = useState({ active: null, shown: false });

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const line = window.innerHeight * 0.3;
      const passed = SECTION_IDS.filter(
        (id) => document.getElementById(id)?.getBoundingClientRect().top <= line,
      );
      const outline = document.getElementById(OUTLINE_ID);
      const shown = Boolean(outline) && outline.getBoundingClientRect().bottom <= 0;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      progressRef.current?.style.setProperty(
        '--htm-progress',
        String(scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0),
      );
      const active = passed.at(-1) ?? null;
      setState((prev) => (prev.active === active && prev.shown === shown ? prev : { active, shown }));
    };
    const onChange = () => {
      frame ??= window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [progressRef]);

  return state;
};

const SectionNav = () => {
  const navRef = useRef(null);
  const chipsRef = useRef(null);
  const { active, shown } = useSectionNav(navRef);
  const ideaIndex = IDEA_IDS.indexOf(active);

  useEffect(() => {
    const list = chipsRef.current;
    const chip = list?.querySelector('.active');
    if (!chip || list.scrollWidth <= list.clientWidth) return;
    // Scroll the chip strip only, scrollIntoView would also move the page.
    list.scrollTo({ left: chip.offsetLeft - 20, behavior: 'smooth' });
  }, [active]);

  return (
    <nav
      ref={navRef}
      className={classNames('htm-nav', { 'htm-nav--shown': shown })}
      aria-label="On this page"
    >
      <Container className="htm-nav-inner">
        <ul className="htm-nav-list lnd-desk">
          {NAV_LINKS.map((link) => {
            const current = link.ids.includes(active);
            return (
              <li key={link.label}>
                <a
                  href={`#${link.ids[0]}`}
                  className={classNames('htm-nav-link', { active: current })}
                  aria-current={current ? 'location' : undefined}
                >
                  {link.label}
                  {current && link.ids.length > 1 && (
                    <span className="htm-nav-count">
                      {ideaIndex + 1} of {IDEA_IDS.length}
                    </span>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
        <ul className="htm-nav-chips lnd-phone" ref={chipsRef}>
          {NAV_CHIPS.map((chip) => (
            <li key={chip.id}>
              <a
                href={`#${chip.id}`}
                className={classNames('htm-nav-chip', { active: active === chip.id })}
                aria-current={active === chip.id ? 'location' : undefined}
              >
                {chip.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="htm-nav-cta lnd-desk">
          <p className="htm-nav-hint">Softcover or Hardcover</p>
          <Link href={BOOK_HREF} className="lnd-btn htm-btn-small" onClick={track('create', 'nav', BOOK_HREF)}>
            Create your photo book
          </Link>
        </div>
      </Container>
      <span className="htm-nav-progress" aria-hidden="true" />
    </nav>
  );
};

//
// Hero and outline
//

const BREADCRUMB = [
  { name: 'Home', url: '/' },
  { name: 'Photo Books', url: '/photo-books' },
  { name: 'How to Make a Photo Book' },
];

const HERO_FACTS = [
  { icon: 'layoutGrid', label: '13 page layouts' },
  { icon: 'book', label: '4 cover designs' },
  { icon: 'copy', label: '26 pages included, up to 100' },
];

const HERO_LAYOUTS = [
  { layout: 'full', label: 'Full page' },
  { layout: 'portrait', label: 'Portrait', selected: true },
  { layout: 'sideBySide', label: '2 photos' },
  { layout: 'bigLeft', label: '3 photos' },
];

const LayoutCard = () => (
  <div className="htm-layout-card" aria-hidden="true">
    <p className="htm-layout-card-page">Page 3</p>
    <p className="htm-layout-card-title">Choose a layout</p>
    <ul>
      {HERO_LAYOUTS.map((item) => (
        <li key={item.label} className={classNames({ selected: item.selected })}>
          <Page layout={item.layout} />
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Hero = () => (
  <section className="lnd-hero htm-hero">
    <Container className="htm-hero-inner">
      <div className="htm-hero-copy">
        <Breadcrumb items={BREADCRUMB} />
        <h1 className="lnd-h1 htm-h1">How to make a photo book that looks polished</h1>
        <p className="htm-lead">
          <Responsive
            desktop="A good book rarely needs more photos. It needs the strongest ones, given room, in a calm order. Here is how to choose and lay them out, page by page, with the layouts in every Parabo Press Softcover and Hardcover book."
            phone="A good book rarely needs more photos. It needs the strongest ones, given room, in a calm order. Here is how to lay them out, page by page."
          />
        </p>
        <div className="htm-hero-actions">
          <ArrowButton href={BOOK_HREF} onClick={track('create', 'hero', BOOK_HREF)}>
            Create your photo book
          </ArrowButton>
          <a
            href={`#${ID.LAYOUTS}`}
            className="htm-text-link lnd-desk"
            onClick={track('layouts', 'hero', `#${ID.LAYOUTS}`)}
          >
            See all the layouts
            <PdpIcon name="arrowDown" size={16} />
          </a>
        </div>
        <ul className="htm-hero-facts lnd-desk">
          {HERO_FACTS.map((fact) => (
            <li key={fact.label}>
              <PdpIcon name={fact.icon} size={16} />
              {fact.label}
            </li>
          ))}
        </ul>
        <ul className="htm-hero-facts lnd-phone">
          <li>
            <PdpIcon name="layoutGrid" size={18} />
            13 page layouts and 4 cover designs
          </li>
          <li>
            <PdpIcon name="copy" size={18} />
            26 pages included, up to 100
          </li>
        </ul>
      </div>
      <div className="htm-hero-scene">
        <div className="htm-hero-photo">
          <Picture
            image={IMAGES.hero}
            sizes="(min-width: 992px) 520px, 100vw"
            alt="Hands holding an open Hardcover Photo Book with a full page photo on each side"
            priority
          />
        </div>
        <LayoutCard />
      </div>
    </Container>
  </section>
);

const Outline = () => {
  const [open, setOpen] = useState(true);
  return (
    <section id={OUTLINE_ID} className="htm-outline" aria-labelledby="htm-outline-title">
      <Container>
        <div className="htm-outline-head">
          <div>
            <p className="htm-eyebrow">In this guide</p>
            <h2 id="htm-outline-title" className="htm-outline-title">
              <Responsive desktop="From choosing photos to the finished book" phone="10 sections" />
            </h2>
          </div>
          <p className="htm-outline-note lnd-desk">10 sections · jump to any part</p>
          <button
            type="button"
            className="htm-outline-toggle lnd-phone"
            aria-expanded={open}
            aria-controls="htm-outline-groups"
            aria-label={open ? 'Collapse the outline' : 'Expand the outline'}
            onClick={() => setOpen(!open)}
          >
            <PdpIcon name={open ? 'chevronUp' : 'chevronDown'} size={22} />
          </button>
        </div>
        <div id="htm-outline-groups" className={classNames('htm-outline-groups', { closed: !open })}>
          {OUTLINE.map(({ group, items }) => (
            <div key={group} className="htm-outline-group">
              <h3 className="htm-outline-group-title">{group}</h3>
              <ol>
                {items.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span className="htm-outline-number">{sectionNumber(item.id)}</span>
                      <span className="htm-outline-label">{item.label}</span>
                      <PdpIcon name="arrowDown" size={16} className="lnd-phone" />
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

//
// 01 Advisor checks
//

const CHECKS = [
  {
    title: 'Pick one from every burst',
    text: 'Keep the frame with open eyes and the best light. Five near copies of one moment flatten a page.',
  },
  {
    title: 'Give your best photo a page',
    text: 'A full page tells the reader this moment matters. Save it for the photo you want them to stop on.',
  },
  {
    title: 'Pair photos that belong together',
    text: 'Match facing pages by moment, place or color, so each spread reads as one idea.',
  },
  {
    title: 'Mix busy pages with calm ones',
    text: 'After three or four photos on a page, let the eye rest on one image and some white space.',
  },
  {
    title: 'Crop with care',
    text: 'Keep faces and hands whole. Make the subject big enough to read at arm’s length.',
  },
  {
    title: 'Add words only when they help',
    text: 'A date, a place or a name. One short line, or leave the page quiet.',
  },
];

const Checks = () => (
  <section id={ID.CHECKS} className="htm-section">
    <Container className="htm-checks">
      <div>
        <SectionHead
          number={sectionNumber(ID.CHECKS)}
          eyebrow="Photo book advisor"
          title="Six checks before you order"
          intro="Run through them on any book, a trip, a wedding or a whole year. Each one has an example further down the page."
          phoneIntro="Run through them on any book. Each one has an example further down."
        />
        <p className="htm-aside">
          Still sorting your camera roll?{' '}
          <a href={CAMERA_ROLL_ARTICLE} onClick={track('camera_roll_article', 'checks', CAMERA_ROLL_ARTICLE)}>
            Start with our camera roll guide
          </a>
        </p>
      </div>
      <ol className="htm-check-list">
        {CHECKS.map((check, index) => (
          <li key={check.title}>
            <span className="htm-check-number" aria-hidden="true">
              {index + 1}
            </span>
            <div>
              <h3 className="htm-h3">{check.title}</h3>
              <p className="htm-body">{check.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

//
// 02 Before and after
//

const BEFORE_AFTER = [
  {
    kind: 'before',
    badge: 'Before',
    summary: '7 photos on 2 busy pages',
    image: '/static/img/photo-book-guide-before.webp',
    alt: 'A photo book spread with four near copies of a dog on the beach on the left page and three small beach photos on the right page',
    notes: [
      { text: 'Four near copies of the same moment' },
      { text: 'Three small photos with no clear lead', desktopOnly: true },
      { text: 'Nowhere for the eye to rest' },
    ],
  },
  {
    kind: 'after',
    badge: 'After',
    summary: '3 photos, one clear moment',
    image: '/static/img/photo-book-guide-after.webp',
    alt: 'The same spread with one dog photo on the full left page and two wide beach views stacked on the right page',
    notes: [
      { text: 'Kept the best of the four near copies', phone: 'Kept the best of the four, on a full page' },
      { text: 'Gave it the full left page', desktopOnly: true },
      { text: 'Paired two wide views of the same beach on the right', phone: 'Paired two wide views of the same beach' },
    ],
  },
];

const BeforeAfter = () => (
  <section id={ID.BEFORE_AFTER} className="htm-section htm-cream">
    <Container className="htm-idea htm-idea--tip-top">
      <SectionHead
        number={sectionNumber(ID.BEFORE_AFTER)}
        eyebrow="Before and after"
        title="Same beach day, two different spreads"
        intro="Both spreads come from one afternoon. The first uses every shot. The second keeps three and gives the best one room."
      />
      <AdvisorTip className="htm-idea-tip">
        Not sure which of four similar shots to keep? Pick the one you would send to family first.
      </AdvisorTip>
      <div className="htm-compare htm-idea-body">
        {BEFORE_AFTER.map((side) => (
          <figure key={side.kind} className={`htm-compare-side htm-compare-side--${side.kind}`}>
            <figcaption className="htm-compare-label">
              <span className="htm-badge">{side.badge}</span>
              {side.summary}
            </figcaption>
            <img
              className="htm-compare-image"
              src={side.image}
              alt={side.alt}
              width="1120"
              height="561"
              loading="lazy"
            />
            <ul className="htm-notes">
              {side.notes.map((note) => (
                <li key={note.text} className={classNames({ 'lnd-desk': note.desktopOnly })}>
                  <PdpIcon name={side.kind === 'after' ? 'check' : 'minus'} size={16} />
                  <Responsive desktop={note.text} phone={note.phone} />
                </li>
              ))}
            </ul>
          </figure>
        ))}
      </div>
    </Container>
  </section>
);

//
// 03 Every layout
//

const L = (layout, name, hint, phoneHint) => ({ layout, name, hint, phoneHint });

const BACK_COVER = L('back', 'Back cover', 'A clean back with a small Parabo Press logo.', 'Small Parabo Press logo.');

// Desktop rows pair a wide group with a narrow one, phones swipe one group per row. Covers and
// the back cover share a row on phones, four photos moves after three photos.
const LAYOUT_GROUPS = [
  {
    title: 'Covers',
    phoneTitle: 'Covers and back',
    span: 4,
    tiles: [
      L('coverBorder', 'Border', 'A white frame around one photo. Calm and classic.', 'A white frame around one photo.'),
      L('coverFull', 'Full width', 'Photo edge to edge. Pick one with space at the edges.', 'Photo edge to edge.'),
      L('coverBorderTitle', 'Border & title', 'A framed photo with a title line under it.', 'A framed photo with a title line.'),
      L('coverFullTitle', 'Full width & title', 'A wide photo on top, the title below.', 'A wide photo, the title below.'),
    ],
    phoneExtra: BACK_COVER,
  },
  { title: 'Back', span: 1, tiles: [BACK_COVER], desktopOnly: true },
  {
    title: 'One photo',
    span: 4,
    tiles: [
      L('full', '1 photo, full page', 'Your strongest photo, edge to edge.'),
      L('square', '1 photo, square', 'A square crop with a white border. Good for phone photos.', 'A square crop with a white border.'),
      L('portrait', '1 photo, portrait', 'A tall photo kept whole, with white space around it.', 'A tall photo kept whole.'),
      L('landscape', '1 photo, landscape', 'A wide view kept whole.'),
    ],
  },
  {
    title: 'Four photos',
    span: 1,
    phoneLate: true,
    tiles: [L('grid', '4 photos, grid', 'Small moments from one day. Use it sparingly.', 'Small moments from one day.')],
  },
  {
    title: 'Two photos',
    span: 2,
    tiles: [
      L('sideBySide', '2 photos, side by side', 'Two people, or a face and a detail.'),
      L('stacked', '2 photos, stacked', 'Two wide shots, like before and after.'),
    ],
  },
  {
    title: 'Three photos',
    span: 3,
    tiles: [
      L('bigLeft', '3 photos, big left', 'One lead photo and two details.'),
      L('bigRight', '3 photos, big right', 'The same idea, mirrored for the facing page.', 'The same, mirrored.'),
      L('row', '3 photos in a row', 'Three steps of one moment, in order.', 'Three steps of one moment.'),
    ],
  },
  {
    title: 'Photos with a caption',
    phoneLate: true,
    span: 3,
    tiles: [
      L('fullCaption', 'Full page + caption', 'A big moment with the place or date under it.', 'A big moment with the place or date.'),
      L('portraitCaption', 'Portrait + caption', 'A portrait with a short note beside it.', 'A portrait with a short note.'),
      L('stackedCaption', '2 photos stacked + caption', 'Two views of one place, plus where it was.', 'Two views of one place, plus where.'),
    ],
  },
];

const LayoutTile = ({ tile, className }) => (
  <li className={classNames('htm-layout', className)}>
    <span className="htm-layout-stage">
      <Page layout={tile.layout} />
    </span>
    <h4 className="htm-layout-name">{tile.name}</h4>
    <p className="htm-layout-hint">
      <Responsive desktop={tile.hint} phone={tile.phoneHint} />
    </p>
  </li>
);

LayoutTile.propTypes = {
  tile: PropTypes.shape({
    layout: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    hint: PropTypes.string.isRequired,
    phoneHint: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
};

const Layouts = () => (
  <section id={ID.LAYOUTS} className="htm-section">
    <Container>
      <SectionHead
        number={sectionNumber(ID.LAYOUTS)}
        eyebrow="The layouts"
        title="Every layout in a Softcover or Hardcover book"
        intro="4 covers and 13 page layouts. Switch any page in the editor and your photos move with it. A good book uses a handful of them, not all."
        phoneIntro="4 covers and 13 page layouts. Switch any page in the editor and your photos move with it. Swipe each row."
      />
      <div className="htm-layout-groups">
        {LAYOUT_GROUPS.map((group) => (
          <div
            key={group.title}
            className={classNames('htm-layout-group', `htm-span-${group.span}`, {
              'lnd-desk': group.desktopOnly,
              'htm-layout-group--late': group.phoneLate,
            })}
          >
            <h3 className="htm-layout-group-title">
              <Responsive desktop={group.title} phone={group.phoneTitle} />
              <span className="htm-layout-count">
                <Responsive
                  desktop={group.tiles.length}
                  phone={group.phoneExtra ? group.tiles.length + 1 : undefined}
                />
              </span>
            </h3>
            <ul className="htm-layout-tiles">
              {group.tiles.map((tile) => (
                <LayoutTile key={tile.name} tile={tile} />
              ))}
              {group.phoneExtra && <LayoutTile tile={group.phoneExtra} className="lnd-phone" />}
            </ul>
          </div>
        ))}
        <AdvisorTip className="htm-span-2 htm-layout-tip">
          Start with photos only. Add a caption page where a date, a place or a name changes how the
          photo reads.
        </AdvisorTip>
      </div>
    </Container>
  </section>
);

//
// 04-08 Layout ideas
//

const BigOrSmall = () => (
  <section id={ID.BIG_OR_SMALL} className="htm-section htm-cream">
    <Container className="htm-big-small">
      <div className="htm-big-small-copy">
        <SectionHead
          number={sectionNumber(ID.BIG_OR_SMALL)}
          eyebrow="Layout idea"
          title="One big photo, or a few small ones"
          intro="Size tells the reader what matters. A single photo on a page reads as the moment itself. Small photos together read as the details around it."
          phoneIntro="Size tells the reader what matters. A single photo reads as the moment itself. Small photos together read as the details around it."
        />
        <ul className="htm-arrow-list lnd-desk">
          <li>
            <PdpIcon name="arrowRight" size={16} />
            Go big for faces, wide views and the photo you want people to stop on.
          </li>
          <li>
            <PdpIcon name="arrowRight" size={16} />
            Go small for a run of little moments from the same day.
          </li>
        </ul>
        <AdvisorTip className="htm-big-small-tip">
          Small photos print small. In a 6×6 book, each photo in the three-in-a-row layout is under 2
          inches wide. Keep group shots for the bigger layouts.
        </AdvisorTip>
      </div>
      <ul className="htm-big-small-pages">
        <li>
          <Page layout="full" photos={[PHOTO.cake]} />
          <p className="htm-figure-title">One big photo</p>
          <p className="htm-figure-sub">1 photo, full page</p>
        </li>
        <li>
          <Page layout="row" photos={[PHOTO.kitchen, PHOTO.baking, PHOTO.bow]} />
          <p className="htm-figure-title">Three small ones</p>
          <p className="htm-figure-sub">3 photos in a row</p>
        </li>
      </ul>
    </Container>
  </section>
);

const PAIRS = [
  {
    title: 'By moment',
    text: 'The same people, minutes apart.',
    left: { layout: 'portrait', photos: [PHOTO.grandpaBlossom] },
    right: { layout: 'portrait', photos: [PHOTO.grandpaGarden] },
  },
  {
    title: 'By place',
    text: 'A wide view, then two people in it.',
    left: { layout: 'landscape', photos: [PHOTO.beachWide] },
    right: { layout: 'sideBySide', photos: [PHOTO.gardenWork, PHOTO.beachSelfie] },
  },
  {
    title: 'By color',
    text: 'Greens that carry across the fold.',
    left: { layout: 'stacked', photos: [PHOTO.hills, PHOTO.tree] },
    right: { layout: 'square', photos: [PHOTO.forest] },
  },
];

const Pairs = () => (
  <section id={ID.PAIRS} className="htm-section">
    <Container className="htm-idea htm-idea--tip-top">
      <SectionHead
        number={sectionNumber(ID.PAIRS)}
        eyebrow="Layout idea"
        title="Pair facing pages that belong together"
        intro="Open a book and you see two pages at once. Give them one shared thread, so the spread reads as a single idea."
        phoneIntro="Open a book and you see two pages at once. Give them one shared thread, so the spread reads as one idea."
      />
      <AdvisorTip className="htm-idea-tip">
        Not sure two photos go together? Look at them side by side at thumbnail size. If one fights
        for attention, give it its own spread.
      </AdvisorTip>
      <ul className="htm-swipe htm-pairs htm-idea-body">
        {PAIRS.map((pair) => (
          <li key={pair.title}>
            <Spread left={pair.left} right={pair.right} />
            <h3 className="htm-figure-title">{pair.title}</h3>
            <p className="htm-figure-sub">{pair.text}</p>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const CROPS = [
  {
    title: 'Wide photo, square spot',
    text: 'A full page crops the sides of a wide group photo. The landscape layout keeps everyone in.',
    before: { layout: 'full', photos: [PHOTO.group] },
    after: { layout: 'landscape', photos: [PHOTO.group] },
  },
  {
    title: 'Group shot, tiny spot',
    text: 'In a four-photo grid every face shrinks. Give the group its own page.',
    before: { layout: 'grid', photos: [PHOTO.family, PHOTO.dog, PHOTO.hat, PHOTO.candle] },
    after: { layout: 'landscape', photos: [PHOTO.family] },
  },
  {
    title: 'Too much on one page',
    text: 'Four equal photos compete. Pick a lead and let two details support it.',
    before: { layout: 'grid', photos: [PHOTO.puppy, PHOTO.dog, PHOTO.hat, PHOTO.candle] },
    after: { layout: 'bigLeft', photos: [PHOTO.dog, PHOTO.hat, PHOTO.candle] },
  },
];

const Crop = () => (
  <section id={ID.CROP} className="htm-section htm-cream">
    <Container className="htm-idea htm-idea--tip-top">
      <SectionHead
        number={sectionNumber(ID.CROP)}
        eyebrow="Layout idea"
        title="Crop with care and leave some room"
        intro="Most pages that feel off have a crop problem. Match the layout to the photo’s shape and give the subject space."
      />
      <AdvisorTip className="htm-idea-tip">
        Before you order, page through once and look only at faces. Every head and chin should be
        inside the photo.
      </AdvisorTip>
      <ul className="htm-swipe htm-crops htm-idea-body">
        {CROPS.map((crop) => (
          <li key={crop.title} className="htm-crop">
            <div className="htm-crop-pages">
              <div>
                <Page {...crop.before} />
                <p className="htm-crop-label">
                  <PdpIcon name="x" size={14} />
                  Before
                </p>
              </div>
              <PdpIcon name="arrowRight" size={16} className="htm-crop-arrow" />
              <div className="htm-crop-after">
                <Page {...crop.after} />
                <p className="htm-crop-label">
                  <PdpIcon name="check" size={14} />
                  After
                </p>
              </div>
            </div>
            <h3 className="htm-figure-title">{crop.title}</h3>
            <p className="htm-body">{crop.text}</p>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

// `photos` is the photo count of the spread, one dot per photo.
const PACE = [
  {
    title: 'Opener',
    mood: 'Calm',
    left: { layout: 'fullCaption', photos: [PHOTO.coast], caption: 'Summer at the coast' },
    right: { layout: 'portrait', photos: [PHOTO.couple] },
  },
  {
    title: 'The details',
    mood: 'Busy',
    busy: true,
    left: { layout: 'bigLeft', photos: [PHOTO.beachDog, PHOTO.beachSelfie, PHOTO.dunes] },
    right: { layout: 'grid', photos: [PHOTO.kitchen, PHOTO.candle, PHOTO.baking, PHOTO.bow] },
  },
  {
    title: 'A breather',
    mood: 'Calm',
    left: { layout: 'landscape', photos: [PHOTO.beachWide] },
    right: { layout: 'portraitCaption', photos: [PHOTO.hat], caption: 'Hats on, all week' },
  },
  {
    title: 'The big moment',
    mood: 'Bold',
    left: { layout: 'full', photos: [PHOTO.cake] },
    right: { layout: 'full', photos: [PHOTO.kids] },
  },
  {
    title: 'A pair',
    mood: 'Lively',
    left: { layout: 'sideBySide', photos: [PHOTO.grandpaBlossom, PHOTO.grandpaGarden] },
    right: { layout: 'stacked', photos: [PHOTO.hills, PHOTO.tree] },
  },
  {
    title: 'Closer',
    mood: 'Calm',
    left: { layout: 'landscape', photos: [PHOTO.lake] },
    right: { layout: 'square', photos: [PHOTO.sunset] },
  },
];

const Pace = () => (
  <section id={ID.PACE} className="htm-section htm-pace-section">
    <Container className="htm-idea htm-idea--tip-top">
      <SectionHead
        number={sectionNumber(ID.PACE)}
        eyebrow="Layout idea"
        title="Pace the book like a story"
        intro="Page through and feel the rhythm. A busy spread feels richer after a quiet one, and a big photo lands harder after a page of details."
        phoneIntro="A busy spread feels richer after a quiet one, and a big photo lands harder after a page of details."
      />
      <AdvisorTip className="htm-idea-tip">
        Open on one strong photo and close on a quiet one. Everything in between can move faster.
      </AdvisorTip>
      <ol className="htm-swipe htm-pace htm-idea-body">
        {PACE.map((spread, index) => {
          const count = spread.left.photos.length + spread.right.photos.length;
          return (
            <li key={spread.title}>
              <Spread left={spread.left} right={spread.right} />
              <span className={classNames('htm-dots', { 'htm-dots--busy': spread.busy })} aria-hidden="true">
                {Array.from({ length: count }, (_, dot) => (
                  <span key={dot} />
                ))}
              </span>
              <h3 className="htm-figure-title">
                {index + 1}. {spread.title}
              </h3>
              <p className="htm-figure-sub">
                {spread.mood} · {count} photos
              </p>
            </li>
          );
        })}
      </ol>
    </Container>
  </section>
);

const CAPTIONS = [
  { text: 'Lake Tahoe, July 2025', why: 'Place and date', good: true },
  { text: 'Grandpa’s 80th', why: 'Names the moment', good: true },
  { text: 'Ellie, 3 days old', why: 'A name and an age', good: true },
  { text: 'Me and everyone at the lake having so much fun!!', why: 'Too long, and the photo already shows it' },
];

const Captions = () => (
  <section id={ID.CAPTIONS} className="htm-section htm-cream">
    <Container className="htm-captions">
      <SectionHead
        number={sectionNumber(ID.CAPTIONS)}
        eyebrow="Layout idea"
        title="Add words only when they help"
        intro="A caption should tell the reader something the photo can’t. A date, a place, a name or an age. If the photo says it already, leave the page quiet."
        phoneIntro="A caption should tell the reader something the photo can’t. A date, a place, a name or an age."
      />
      <figure className="htm-captions-photo">
        <div className="htm-captions-image">
          <Picture
            image={IMAGES.captions}
            sizes="(min-width: 992px) 520px, 100vw"
            alt="An open Softcover Photo Book with a short caption printed under each photo"
          />
        </div>
        <figcaption>
          <Responsive
            desktop="Softcover Photo Book, captions printed under each photo"
            phone="Softcover Photo Book, captions under each photo"
          />
        </figcaption>
      </figure>
      <div className="htm-captions-side">
        <ul className="htm-caption-list">
          {CAPTIONS.map((caption) => (
            <li key={caption.text} className={classNames({ bad: !caption.good })}>
              <PdpIcon name={caption.good ? 'check' : 'x'} size={16} />
              <span className="htm-caption-text">{caption.text}</span>
              <span className="htm-caption-why">
                <span className="sr-only">{caption.good ? 'Good: ' : 'Skip: '}</span>
                {caption.why}
              </span>
            </li>
          ))}
        </ul>
        <AdvisorTip>
          Write captions last, once the layout is set. Keep each to one short line and skip emoji,
          which may not print correctly.
        </AdvisorTip>
      </div>
    </Container>
  </section>
);

//
// 09 In the editor
//

const EDITOR_STEPS = [
  {
    title: 'Open Layout under a page',
    text: 'On the web it sits under each page. In the app, tap the layout icon above it.',
    phone: 'In the app, tap the layout icon above it. On the web it sits under each page.',
  },
  {
    title: 'Pick photos only or with text',
    text: 'Each preview uses your own photos, so you see the page before you choose.',
  },
  {
    title: 'Your photos move with you',
    text: 'They carry over to the new layout and each crop resets. A layout with more spots leaves one empty. With fewer spots, the extra photos leave the page but stay with your book’s photos. Check the page after you switch.',
  },
];

const Editor = () => (
  <section id={ID.EDITOR} className="htm-section htm-mint">
    <Container className="htm-editor">
      <div className="htm-editor-copy">
        <SectionHead
          number={sectionNumber(ID.EDITOR)}
          eyebrow="In the editor"
          title="Change any page’s layout in a few taps"
          intro="We lay out your photos first. Then you change the pages that deserve more room, on the web or in the iPhone app."
          phoneIntro="We lay out your photos first. Then you change the pages that deserve more room, in the iPhone app or on the web."
        />
        <ol className="htm-steps">
          {EDITOR_STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="htm-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <h3 className="htm-h3">{step.title}</h3>
                <p className="htm-body">
                  <Responsive desktop={step.text} phone={step.phone} />
                </p>
              </div>
            </li>
          ))}
        </ol>
        <ArrowButton
          href={BOOK_HREF}
          className="lnd-desk htm-editor-cta"
          onClick={track('create', 'editor', BOOK_HREF)}
        >
          Create your photo book
        </ArrowButton>
      </div>
      <div className="htm-editor-shots">
        <figure className="htm-editor-web lnd-desk">
          <img
            src={SCREENS.web}
            alt="The Choose a layout panel in the Parabo Press web editor, with previews of the page in each layout"
            width="610"
            height="1338"
            loading="lazy"
          />
          <figcaption>
            <PdpIcon name="monitor" size={16} />
            On the web
          </figcaption>
        </figure>
        <figure className="htm-editor-app">
          <div className="htm-phone">
            <img
              src={SCREENS.app}
              alt="Parabo Press iPhone app showing the Change Layout panel for a Softcover Photo Book page"
              width="480"
              height="1043"
              loading="lazy"
            />
          </div>
          <figcaption className="lnd-desk">
            <PdpIcon name="smartphone" size={16} />
            In the iPhone app
          </figcaption>
        </figure>
      </div>
    </Container>
  </section>
);

//
// 10 FAQ, final CTA
//

// Shape ProductFaq renders. `description` is HTML, the FAQPage JSON-LD gets it without tags.
const FAQ = [
  {
    caption: 'How many photos fit on a page?',
    description:
      'Up to four, depending on the layout. Most pages look best with one or two, and a book reads better when you mix them.',
  },
  {
    caption: 'How many pages can my book have?',
    description:
      'Softcover and Hardcover Photo Books include 26 pages and go up to 100. Premium Hardcover Photo Books start at 20 pages and go up to 350.',
  },
  {
    caption: 'Can I change a layout after I add photos?',
    description:
      'Yes. Open Layout under any page and pick another. Your photos carry over and each crop resets, so check the page after you switch. If the new layout has fewer spots, the extra photos leave the page but stay with your book’s photos.',
  },
  {
    caption: 'Can I add text to my photo book?',
    description:
      'Yes. Add a title on the cover and a caption on any page with a caption layout. Keep it short, up to 32 characters.',
  },
  {
    caption: 'Can I make a photo book on my iPhone?',
    description:
      'Yes. Make a Softcover or Hardcover Photo Book in the Parabo Press iPhone app, or create one on the web.',
  },
  {
    caption: 'Which book should I choose?',
    description:
      '<a href="/softcover-books">Softcover</a> comes in 8x8 or as a <a href="/mini-photo-books">6x6 mini photo book</a>, and is light enough to carry. The <a href="/hardcover-book">8x8 Hardcover</a> is sturdier. <a href="/premium-hardcover-books">Premium Hardcover</a> comes in larger landscape sizes with a photo or vegan leather cover.',
  },
];

const FAQ_JSON_LD = FAQ.map(({ caption, description }) => ({
  question: caption,
  answer: description.replace(/<[^>]+>/g, ''),
}));

const Faq = () => (
  <section id={ID.FAQ} className="htm-section htm-faq">
    <Container>
      <ProductFaq
        eyebrow={`${sectionNumber(ID.FAQ)} · Questions`}
        title="Questions about making a photo book"
        intro={false}
        items={FAQ}
      />
    </Container>
  </section>
);

// iPhone and iPad (iPadOS reports a Mac with touch). Same rule as Baby Photo Books. Rendered as
// iPhone on the server, so the App Store badge is in the first paint there.
const isAppleMobile = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const useWebOnly = () => {
  const [webOnly, setWebOnly] = useState(false);
  useEffect(() => setWebOnly(!isAppleMobile()), []);
  return webOnly;
};

// Desktop: web button + QR to the App Store. iPhone: App Store badge + web link. Other phones:
// the web button only.
const FinalCta = () => {
  const webOnly = useWebOnly();
  return (
    <section className="htm-section htm-cream htm-final">
      <Container className="htm-final-inner">
        <div className="htm-final-copy">
          <h2 className="htm-h2">Start with one favorite photo</h2>
          <p className="htm-intro">
            Pick a book and add your photos. We lay them out first, then you give the best ones the
            room they deserve.
          </p>
          <div className={classNames('htm-cta', { 'htm-cta--web-only': webOnly })}>
            <ArrowButton
              href={BOOK_HREF}
              className="htm-cta-web"
              onClick={track('create', 'final', BOOK_HREF)}
            >
              Create your photo book
            </ArrowButton>
            <Anchor
              href={APP_STORE_HREF}
              className="htm-qr htm-cta-desk"
              targetBlank
              onClick={track('app_store', 'final', 'app_store')}
            >
              <span className="htm-qr-code">
                <img
                  src="/static/img/photo-books-app-qr.svg"
                  alt="QR code for the Parabo Press app on the App Store"
                  width="68"
                  height="68"
                />
              </span>
              <span className="htm-qr-label">
                Get the iPhone app.
                <br />
                Scan with your iPhone
              </span>
            </Anchor>
            <Anchor
              href={APP_STORE_HREF}
              className="htm-badge-link htm-cta-app"
              targetBlank
              onClick={track('app_store', 'final', 'app_store')}
            >
              <img
                src="/static/img/app-store-badge.svg"
                alt="Download Parabo Press on the App Store"
                width="162"
                height="54"
              />
            </Anchor>
            <Link
              href={BOOK_HREF}
              className="htm-text-link htm-cta-app"
              onClick={track('create', 'final', BOOK_HREF)}
            >
              Create on the web
              <PdpIcon name="arrowRight" size={16} />
            </Link>
          </div>
        </div>
        <div className="htm-final-photo">
          <Picture
            image={IMAGES.final}
            sizes="(min-width: 992px) 560px, 100vw"
            alt="An open Hardcover Photo Book on a couch, a puppy portrait on the left and a couple with the puppy on the right"
          />
        </div>
      </Container>
    </section>
  );
};

const HowToMakeAPhotoBookPage = () => (
  <>
    <Head
      title="How to Make a Photo Book: Layout Ideas That Look Polished"
      description="Choose the right photos and lay them out page by page. Before and after spreads, every Softcover and Hardcover layout, and simple advice for a book that looks polished."
      image={getCloudinaryUrl({ imageKey: IMAGES.hero.imageKey, format: 'jpg' })}
      heroImage={getCloudinaryUrl({ imageKey: IMAGES.hero.imageKey })}
      heroImageSrcSet={getCloudinarySrcSet(IMAGES.hero)}
      heroImageSizes="(min-width: 992px) 520px, 100vw"
    />
    <ScriptMarkupBreadcrumb items={BREADCRUMB} host={SETTINGS.HOST} />
    <ScriptMarkupFaq items={FAQ_JSON_LD} />

    <Layout>
      <div className="lnd htm">
        <Hero />
        <Outline />
        <SectionNav />
        <Checks />
        <BeforeAfter />
        <Layouts />
        <BigOrSmall />
        <Pairs />
        <Crop />
        <Pace />
        <Captions />
        <Editor />
        <Faq />
        <FinalCta />
        <NewsletterTile />
      </div>
    </Layout>
  </>
);

export default HowToMakeAPhotoBookPage;
